import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getStoreList } from "model/dashboardStores/dStoresAPI";
import { logoutSagaAC } from "model/system/systemActionCreators";

import { typeStatisticsState } from "model/dashboardStatistics/dStatisticsReducer";
import { IdStoresStateDataItem } from "model/dashboardStores/dStoresReducer";
import { Loader } from "components/UI/Loader/Loader";
import { Alert } from "components/UI/Alert/Alert";
import { Trans } from "@lingui/macro";
import { Button } from "components/UI/Button/Button";

import { InputCheckbox } from "components/UI/InputCheckbox/InputCheckbox";
import { dStatisticsSetStoriesAC } from "model/dashboardStatistics/dStatisticsActionCreators";

// Types
export type typePopupStories = {
  setOpenStoriesPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

type typeSlimCheckedStore = {
  id: string;
  name: string;
  address: string;
  deleted: boolean;
  checked: boolean;
};

// Component
export const PopupStories: React.FC<typePopupStories> = ({
  setOpenStoriesPopup,
}) => {
  const dispatch = useDispatch();

  // System
  const selectedStories = useSelector(
    (state: { dStatistics: typeStatisticsState }) => state.dStatistics.stories,
  );
  const [loader, setLoader] = useState(true);

  const mappedStoriesList = (
    stories: IdStoresStateDataItem[],
  ): typeSlimCheckedStore[] => {
    return stories.map((item) => {
      return {
        id: item.id,
        name: item.name,
        address: item.address || "",
        deleted: item.deleted,
        checked: selectedStories.length
          ? selectedStories.includes(item.id)
          : true,
      };
    });
  };

  const isAllSelected = (): { all: boolean; noOne: boolean } => {
    const list = storiesList.filter((item) => item.checked);
    return {
      all: list.length === storiesList.length,
      noOne: list.length === 0,
    };
  };

  // Did mount
  useEffect(() => {
    setLoader(true);
    const setData = async () => {
      let responseStores = await getStoreList({
        filters: { name: null, deleted: false },
      });

      if (responseStores.status === 200) {
        setStoriesList(mappedStoriesList(responseStores.data));
        setLoader(false);
      } else if (responseStores.status === 401) {
        setLoader(false);
        dispatch(logoutSagaAC());
      }
    };

    setData().catch(console.error);

    // eslint-disable-next-line
  }, []);

  const [storiesList, setStoriesList] = useState<typeSlimCheckedStore[]>([]);
  const [selectedAll, setSelectedAll] = useState(isAllSelected().all);
  const [selectedNoOne, setSelectedNoOne] = useState(isAllSelected().noOne);

  const onCheckItem = (id: string) => {
    setStoriesList(
      storiesList.map((item) => {
        if (item.id === id) {
          item.checked = !item.checked;
        }
        return item;
      }),
    );
  };

  const onSelectAllHandler = () => {
    const allCheckedStories = storiesList.map((item) => {
      return { ...item, checked: true };
    });
    setStoriesList(allCheckedStories);
  };

  const onClearHandler = () => {
    setStoriesList(() =>
      storiesList.map((item) => {
        return { ...item, checked: false };
      }),
    );
  };

  useEffect(() => {
    if (isAllSelected().all) {
      setSelectedAll(true);
    } else {
      setSelectedAll(false);
    }

    if (isAllSelected().noOne) {
      setSelectedNoOne(true);
    } else {
      setSelectedNoOne(false);
    }
    // eslint-disable-next-line
  }, [storiesList]);

  const onApplyHandler = () => {
    const checkedStories = storiesList
      .filter((item) => item.checked)
      .map((item) => item.id);
    dispatch(dStatisticsSetStoriesAC(checkedStories));
    setOpenStoriesPopup(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6  py-2">
        <div className="relative flex flex-col  gap-2 sm:gap-6">
          <div className="flex flex-wrap gap-2">
            {selectedAll ? (
              <Button
                secondary={true}
                type={"button"}
                label={<Trans>Clear selection</Trans>}
                buttonClassName={"py-1 px-2 text-xs "}
                onClick={onClearHandler}
              />
            ) : (
              <Button
                secondary={true}
                type={"button"}
                label={<Trans>Select all</Trans>}
                buttonClassName={"py-1 px-2 text-xs"}
                onClick={onSelectAllHandler}
              />
            )}
          </div>

          {!!storiesList.length ? (
            <div
              className={`relative grid ${storiesList.length < 3 ? (storiesList.length < 2 ? "grid-cols-1" : "grid-cols-2") : "grid-cols-3"} gap-y-4 gap-x-8 max-w-lg`}
            >
              {storiesList.map((item) => {
                return (
                  <div className="flex basis-1/3" key={item.id}>
                    <InputCheckbox
                      id={item.id}
                      name={item.name}
                      label={item.name}
                      checked={item.checked}
                      onChange={() => onCheckItem(item.id)}
                      labelClassName="text-xl  cursor-pointer"
                      inputClassName="self-center"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <Alert
              type="info"
              title={<Trans>The list of available store is empty.</Trans>}
            />
          )}
          {loader ? (
            <div>
              <Loader />
            </div>
          ) : null}
        </div>
      </div>

      <div className="col-span-2 flex justify-end gap-2">
        <Button
          type="button"
          secondary={true}
          onClick={() => setOpenStoriesPopup(false)}
          label={<Trans>Cancel</Trans>}
        />
        <Button
          type="button"
          disabled={selectedNoOne}
          onClick={onApplyHandler}
          label={<Trans>Apply</Trans>}
        />
      </div>
    </div>
  );
};
